import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Linkedin, Facebook, Instagram, Twitter, Users, ArrowRight } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SOCIAL_ICONS = {
  linkedin: { icon: Linkedin, color: "#0A66C2", label: "LinkedIn" },
  facebook: { icon: Facebook, color: "#1877F2", label: "Facebook" },
  instagram: { icon: Instagram, color: "#E4405F", label: "Instagram" },
  twitter: { icon: Twitter, color: "#1DA1F2", label: "X" },
};

function VolunteerCard({ volunteer }) {
  const socials = ["linkedin", "facebook", "instagram", "twitter"].filter(
    (key) => volunteer[key]
  );

  return (
    <div
      data-testid={`volunteer-${volunteer.id}`}
      className="group bg-slate-900/50 border border-white/10 rounded-2xl overflow-hidden hover:border-blue-500/30 transition-all duration-300 text-center"
    >
      <div className="relative h-56 overflow-hidden">
        {volunteer.photo_url ? (
          <img
            src={volunteer.photo_url}
            alt={`${volunteer.first_name} ${volunteer.last_name}`}
            className="w-full h-full object-cover object-top img-zoom opacity-80 group-hover:opacity-100 transition-opacity"
          />
        ) : (
          <div className="w-full h-full bg-slate-800 flex items-center justify-center">
            <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center">
              <span className="font-heading text-3xl text-blue-400">
                {volunteer.first_name[0]}
              </span>
            </div>
          </div>
        )}
      </div>
      <div className="p-5">
        <h3 className="font-heading text-xl font-medium text-slate-50 mb-1">
          {volunteer.first_name} {volunteer.last_name}
        </h3>
        {volunteer.role && (
          <p className="font-body text-sm text-blue-400 mb-4">{volunteer.role}</p>
        )}
        {socials.length > 0 && (
          <div className="flex justify-center gap-3">
            {socials.map((key) => {
              const { icon: Icon, color, label } = SOCIAL_ICONS[key];
              return (
                <a
                  key={key}
                  href={volunteer[key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`volunteer-${volunteer.id}-${key}`}
                  title={label}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 hover:scale-110"
                  style={{ backgroundColor: `${color}20`, color }}
                >
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default function VolunteersPage() {
  const [volunteers, setVolunteers] = useState([]);

  useEffect(() => {
    axios.get(`${API}/volunteers`).then((r) => setVolunteers(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="volunteers-page" className="pt-24">
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="font-body text-xs md:text-sm font-medium tracking-[0.2em] uppercase text-blue-400 mb-6">
            Notre {"\u00c9"}quipe
          </p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-slate-50 mb-8 leading-[1.1]">
            Les b{"\u00e9"}n{"\u00e9"}voles qui font vivre EDUCPEOPLE
          </h1>
          <p className="font-body text-base md:text-lg font-light text-slate-300 max-w-3xl">
            Notre force, ce sont nos b{"\u00e9"}n{"\u00e9"}voles. Des professionnels engag{"\u00e9"}s qui donnent de leur temps et de leurs comp{"\u00e9"}tences pour l'{"\u00e9"}ducation inclusive.
          </p>
        </div>
      </section>

      <div className="section-divider" />

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {volunteers.length === 0 ? (
            <p className="font-body text-slate-400 text-center py-12">Aucun b{"\u00e9"}n{"\u00e9"}vole pour le moment.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {volunteers.map((v) => (
                <VolunteerCard key={v.id} volunteer={v} />
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="section-divider" />

      <section className="py-24 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <div className="w-16 h-16 rounded-2xl bg-blue-500/20 flex items-center justify-center mx-auto mb-6">
            <Users className="w-8 h-8 text-blue-400" />
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-light text-slate-50 mb-6">
            Rejoindre l'{"\u00e9"}quipe
          </h2>
          <p className="font-body text-base md:text-lg text-slate-400 mb-10 max-w-2xl mx-auto">
            Vous souhaitez contribuer {"\u00e0"} notre mission ? Que vous soyez professionnel du num{"\u00e9"}rique, enseignant ou {"\u00e9"}tudiant, votre engagement fait la diff{"\u00e9"}rence.
          </p>
          <Link
            to="/contact"
            data-testid="volunteer-join-cta"
            className="inline-flex items-center gap-2 bg-blue-500 hover:bg-blue-400 text-white px-8 py-4 rounded-full font-body font-medium transition-all duration-300"
          >
            Devenir b{"\u00e9"}n{"\u00e9"}vole <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
